import type {
  CompetencySlug,
  QuestionType,
  HtsSubmissionStatus,
  ItemScoreHistoryEntry,
  SubstitutionAvailability,
} from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
// =============================================================================

export interface InterviewTemplateItem {
  id: string;
  templateId: string;
  order: number;
  competencyId: CompetencySlug;
  questionType: QuestionType;
  questionText: string;
  sourceQuestionId: string | null;
  /**
   * Whether this item's questionText differs from the one the candidate last
   * answered. Only set by the assessment-scoped endpoints (create/fetch-by-id);
   * absent from the plain `/interview-templates` list. False for a retry that
   * deliberately kept the same question, even a substituted one.
   */
  questionChanged?: boolean;
}

export interface InterviewTemplate {
  id: string;
  userId: number;
  name: string;
  description: string | null;
  unlockPriceCents: number;
  unlockIncludedCredits: number;
  interviewCostCredits: number;
  coachingEnabled: boolean;
  sourcePackageId: string | null;
  creditTransactionId: string;
  purchasedAt: string;
  /** Non-null once revoked — hides retake/start CTAs. */
  revokedAt: string | null;
  htsRequired: boolean;
  sourceHtsQuestionnaireId: string | null;
  htsSubmissionStatus: HtsSubmissionStatus | null;
  /** Whether any HTS submission for this template has ever reached SUBMITTED — used only to pick the launcher's default flow, never to gate anything. */
  htsEverSubmitted: boolean;
  /** Number of HTS questions copied onto this template — null when htsRequired is false. */
  htsQuestionCount: number | null;
  /** Whether this template has ever produced a COMPLETED assessment — the Assessment-side mirror of htsEverSubmitted. */
  assessmentEverCompleted: boolean;
  items: InterviewTemplateItem[];
}

export interface TemplateItemHistory {
  /** One entry per question the candidate has ever answered, carrying their latest attempt at it. */
  itemScoreHistory: ItemScoreHistoryEntry[];
  /** Per template item id, what a "different question" pick would land on right now. */
  substitutionAvailability: Record<string, SubstitutionAvailability>;
}

// =============================================================================
// SERVICE
// =============================================================================

export class InterviewTemplatesService extends BaseAPIClient {
  /** List the current user's purchased interview templates. */
  async list(): Promise<InterviewTemplate[]> {
    return this.get<InterviewTemplate[]>('/interview-templates');
  }

  /** Latest score per question and "different question" availability, across every sitting of a template. */
  async getItemHistory(templateId: string): Promise<TemplateItemHistory> {
    return this.get<TemplateItemHistory>(`/interview-templates/${templateId}/item-history`);
  }
}

export default InterviewTemplatesService;
